import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FolderOpen } from "lucide-react";

interface RequestEvidenceModalProps {
  open: boolean;
  onClose: () => void;
  relatedItem: string;
  onSubmit: (evidenceType: string, description: string, deadline: string) => void;
}

const evidenceTypes = [
  'Policy Document',
  'Approval Record',
  'Test Results',
  'Training Completion Records',
  'Audit Log',
  'Risk Assessment Report',
  'System Configuration Screenshot',
  'Sign-Off Email',
  'Meeting Minutes',
  'Other',
];

export function RequestEvidenceModal({ open, onClose, relatedItem, onSubmit }: RequestEvidenceModalProps) {
  const [evidenceType, setEvidenceType] = useState('');
  const [description, setDescription] = useState('');
  const [deadline, setDeadline] = useState('');

  const handleSubmit = () => {
    if (!evidenceType || !description.trim() || !deadline) return;
    onSubmit(evidenceType, description.trim(), deadline);
    reset();
    onClose();
  };

  const reset = () => {
    setEvidenceType('');
    setDescription('');
    setDeadline('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const isValid = evidenceType && description.trim() && deadline;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="h-5 w-5 text-purple-500" />
            Request Evidence
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-sm text-muted-foreground">Related Item</Label>
            <div className="px-3 py-2 rounded-md border bg-muted/40 text-sm font-medium">
              {relatedItem}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="evidence-type">Evidence Type</Label>
            <Select value={evidenceType} onValueChange={setEvidenceType}>
              <SelectTrigger id="evidence-type">
                <SelectValue placeholder="Select evidence type..." />
              </SelectTrigger>
              <SelectContent>
                {evidenceTypes.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ev-description">Description</Label>
            <Textarea
              id="ev-description"
              placeholder="Describe exactly what evidence is needed and why it is required for this audit..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="deadline">Deadline</Label>
            <Input
              id="deadline"
              type="date"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid}>
            Send Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
